/**
 * Tax Rates Page (Settings)
 * List, create, edit, and delete the tenant's sales tax rates.
 *
 * Rates are entered and shown as percentages (8.5%) but the API stores a
 * Decimal(5,4) fraction (0.0850), so values are converted at the boundary.
 * Only ADMIN users may change tax rates (backend requireRole('ADMIN'));
 * other roles get a read-only table.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { createAPIClient, ITaxRatesApi, IGLAccountsApi } from '../../api';
import * as types from '../../api/types';
import { getErrorMessage } from '../../utils/apiError';
import { canManageTaxRates } from '../../utils/permissions';
import { formatRatePercent, percentToRate, rateToPercentString } from '../../utils/taxRate';
import {
  inputClass,
  labelClass,
  primaryButtonClass,
  secondaryButtonClass,
  rowActionClass,
  rowDangerActionClass,
} from '../../components/formStyles';
import { Toast } from '../../components/Toast';

/** Backend validatePayableAccount: LIABILITY type with CREDIT normal balance. */
function isPayableAccount(account: types.GLAccount): boolean {
  return account.accountType === types.GLAccountType.LIABILITY && account.normalBalance === 'CREDIT';
}

interface FormState {
  name: string;
  percent: string;
  payableAccountId: string;
}

const EMPTY_FORM: FormState = { name: '', percent: '', payableAccountId: '' };

type Editing = { mode: 'create' } | { mode: 'edit'; taxRate: types.TaxRate } | null;

export function TaxRatesPage() {
  const { user } = useAuth();
  const apiClient = React.useMemo<ITaxRatesApi & IGLAccountsApi>(() => createAPIClient(), []);
  const canManage = canManageTaxRates(user);

  // State
  const [taxRates, setTaxRates] = useState<types.TaxRate[]>([]);
  const [glAccounts, setGLAccounts] = useState<types.GLAccount[]>([]);
  const [glAccountsError, setGLAccountsError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<Editing>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const refreshTaxRates = useCallback(async () => {
    const rates = await apiClient.listTaxRates();
    setTaxRates(rates);
  }, [apiClient]);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        setGLAccountsError(null);

        // GL accounts only feed the create/edit form, so only admins need them,
        // and a failure there should not hide the tax rate table.
        const [ratesResult, accountsResult] = await Promise.allSettled([
          apiClient.listTaxRates(),
          canManage ? apiClient.listGLAccounts() : Promise.resolve<types.GLAccount[]>([]),
        ]);

        if (ratesResult.status === 'rejected') {
          throw ratesResult.reason;
        }
        setTaxRates(ratesResult.value);

        if (accountsResult.status === 'fulfilled') {
          setGLAccounts(accountsResult.value);
        } else {
          setGLAccounts([]);
          setGLAccountsError(getErrorMessage(accountsResult.reason, 'Failed to load GL accounts'));
        }
      } catch (err) {
        setError(getErrorMessage(err, 'Failed to load tax rates'));
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [apiClient, canManage]);

  const currentAccountId = editing?.mode === 'edit' ? editing.taxRate.payableAccount.id : null;
  const payableAccounts = glAccounts
    .filter((a) => isPayableAccount(a) || a.id === currentAccountId)
    .sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const openCreate = () => {
    setEditing({ mode: 'create' });
    setForm({
      ...EMPTY_FORM,
      // Preselect when there is exactly one valid account (the usual case).
      payableAccountId: payableAccounts.length === 1 ? payableAccounts[0].id : '',
    });
    setFormError(null);
    setConfirmDeleteId(null);
  };

  const openEdit = (taxRate: types.TaxRate) => {
    setEditing({ mode: 'edit', taxRate });
    setForm({
      name: taxRate.name,
      percent: rateToPercentString(taxRate.rate),
      payableAccountId: taxRate.payableAccount.id,
    });
    setFormError(null);
    setConfirmDeleteId(null);
  };

  const closeForm = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;

    const name = form.name.trim();
    if (!name) {
      setFormError('Name is required');
      return;
    }
    if (name.length > 100) {
      setFormError('Name must be 100 characters or less');
      return;
    }
    const rate = percentToRate(form.percent);
    if (rate === null) {
      setFormError('Enter a rate between 0 and 100 with at most 2 decimal places (e.g. 8.25)');
      return;
    }
    if (!form.payableAccountId) {
      setFormError('Select a tax payable account');
      return;
    }

    const request: types.CreateTaxRateRequest = { name, rate, payableAccountId: form.payableAccountId };

    try {
      setIsSaving(true);
      setFormError(null);

      if (editing.mode === 'edit') {
        await apiClient.updateTaxRate(editing.taxRate.id, request);
      } else {
        await apiClient.createTaxRate(request);
      }

      closeForm();
      await refreshTaxRates();
    } catch (err) {
      setFormError(
        getErrorMessage(err, editing.mode === 'edit' ? 'Failed to update tax rate' : 'Failed to create tax rate')
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (taxRate: types.TaxRate) => {
    try {
      setDeletingId(taxRate.id);
      setError(null);

      await apiClient.deleteTaxRate(taxRate.id);
      setConfirmDeleteId(null);
      if (editing?.mode === 'edit' && editing.taxRate.id === taxRate.id) closeForm();
      await refreshTaxRates();
    } catch (err) {
      // e.g. 400 "Cannot delete tax rate that is linked to properties..."
      setConfirmDeleteId(null);
      setError(getErrorMessage(err, 'Failed to delete tax rate'));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      {/* Title */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
        <div>
          <Link to="/settings" className="text-sm font-semibold text-gray-500 hover:text-blue-600">
            ← Settings
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">Tax Rates</h1>
        </div>
        {canManage && !editing && (
          <button onClick={openCreate} className={`${primaryButtonClass} w-full sm:w-auto min-h-[44px] sm:min-h-0`}>
            Add Tax Rate
          </button>
        )}
      </div>

      {error && <Toast kind="error">{error}</Toast>}

      {/* Add / Edit Form */}
      {canManage && editing && (
        <section className="bg-white rounded-lg shadow mb-6">
          <form onSubmit={handleSubmit} noValidate>
            <div className="p-4 sm:p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">
                {editing.mode === 'edit' ? `Edit ${editing.taxRate.name}` : 'New Tax Rate'}
              </h2>

              {formError && <Toast kind="error">{formError}</Toast>}

              {glAccountsError && <Toast kind="error">{glAccountsError}</Toast>}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
                <div className="sm:col-span-3">
                  <label htmlFor="taxRateName" className={labelClass}>
                    Name<span className="ml-0.5 text-red-600">*</span>
                  </label>
                  <input
                    id="taxRateName"
                    type="text"
                    value={form.name}
                    onChange={(e) => setField('name', e.target.value)}
                    disabled={isSaving}
                    maxLength={100}
                    placeholder="Sales Tax - CA"
                    className={inputClass}
                  />
                </div>

                <div className="sm:col-span-1">
                  <label htmlFor="taxRatePercent" className={labelClass}>
                    Rate<span className="ml-0.5 text-red-600">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="taxRatePercent"
                      type="text"
                      inputMode="decimal"
                      value={form.percent}
                      onChange={(e) => setField('percent', e.target.value)}
                      disabled={isSaving}
                      placeholder="8.5"
                      className={`${inputClass} pr-8`}
                    />
                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">
                      %
                    </span>
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="taxRatePayable" className={labelClass}>
                    Tax payable account<span className="ml-0.5 text-red-600">*</span>
                  </label>
                  <select
                    id="taxRatePayable"
                    value={form.payableAccountId}
                    onChange={(e) => setField('payableAccountId', e.target.value)}
                    disabled={isSaving}
                    className={inputClass}
                  >
                    <option value="">Select an account</option>
                    {payableAccounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.accountNumber} · {account.accountName}
                      </option>
                    ))}
                  </select>
                  {!glAccountsError && payableAccounts.length === 0 && (
                    <p className="mt-1.5 text-xs text-gray-500">
                      No liability accounts with a credit balance found. Set up the chart of accounts first.
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end rounded-b-lg border-t border-gray-200 bg-gray-50 px-4 sm:px-6 py-4">
              <button type="button" onClick={closeForm} disabled={isSaving} className={`${secondaryButtonClass} w-full sm:w-auto min-h-[44px] sm:min-h-0`}>
                Cancel
              </button>
              <button type="submit" disabled={isSaving} className={`${primaryButtonClass} w-full sm:w-auto min-h-[44px] sm:min-h-0`}>
                {isSaving ? 'Saving...' : editing.mode === 'edit' ? 'Save Changes' : 'Create Tax Rate'}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="bg-white rounded-lg shadow p-4 sm:p-6">
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        )}

        {/* Table */}
        {!isLoading &&
          (taxRates.length === 0 ? (
            <p className="text-gray-600 text-center py-8">No tax rates yet</p>
          ) : (
            <div className="md:overflow-x-auto">
              <table className="responsive-table min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payable Account</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Properties</th>
                    {canManage && (
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <span className="sr-only">Actions</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {taxRates.map((taxRate) => {
                    const inUse = taxRate.propertyCount > 0;
                    const isConfirming = confirmDeleteId === taxRate.id;
                    const isDeleting = deletingId === taxRate.id;
                    return (
                      <tr key={taxRate.id}>
                        <td data-label="Name" data-primary className="px-4 py-3 font-semibold text-gray-900">{taxRate.name}</td>
                        <td data-label="Rate" className="px-4 py-3 text-sm text-gray-900">{formatRatePercent(taxRate.rate)}</td>
                        <td data-label="Payable Account" className="px-4 py-3 text-sm text-gray-600">
                          {taxRate.payableAccount.accountNumber} · {taxRate.payableAccount.accountName}
                        </td>
                        <td data-label="Properties" className="px-4 py-3 text-sm text-gray-600">{taxRate.propertyCount}</td>
                        {canManage && (
                          <td className="px-4 py-3 text-right md:whitespace-nowrap">
                            {isConfirming ? (
                              <span className="inline-flex flex-wrap items-center gap-x-4 gap-y-2 sm:gap-3">
                                <span className="text-sm text-gray-700">Delete this tax rate?</span>
                                <button
                                  onClick={() => handleDelete(taxRate)}
                                  disabled={isDeleting}
                                  className={rowDangerActionClass}
                                >
                                  {isDeleting ? 'Deleting...' : 'Confirm'}
                                </button>
                                <button
                                  onClick={() => setConfirmDeleteId(null)}
                                  disabled={isDeleting}
                                  className={rowActionClass}
                                >
                                  Cancel
                                </button>
                              </span>
                            ) : (
                              <span className="inline-flex flex-wrap items-center gap-4 py-1 md:py-0">
                                <button
                                  onClick={() => openEdit(taxRate)}
                                  disabled={isSaving}
                                  className={rowActionClass}
                                >
                                  Edit
                                </button>
                                {/* Tooltip on a wrapper: disabled buttons get no hover events in some browsers. */}
                                <span
                                  title={
                                    inUse
                                      ? `Used by ${taxRate.propertyCount} ${taxRate.propertyCount === 1 ? 'property' : 'properties'}; reassign them before deleting`
                                      : undefined
                                  }
                                >
                                  <button
                                    onClick={() => setConfirmDeleteId(taxRate.id)}
                                    disabled={inUse || deletingId !== null}
                                    aria-label={inUse ? `Delete ${taxRate.name} (in use by properties)` : `Delete ${taxRate.name}`}
                                    className={rowDangerActionClass}
                                  >
                                    Delete
                                  </button>
                                </span>
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
      </section>
    </main>
  );
}
